/** @shell standard @header bar */
import React, { useEffect, useState, useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import Animated from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ScreenHeader, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { MainLoader } from '../components/load-main/MainLoader';
import { CmsPagesApiHtmlScroll } from '../components/common/CmsPagesApiHtmlScroll';
import { cmsPageHtmlToDisplayText } from '../shared/utils/htmlToPlainText';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { fetchCmsPages, findCmsPageByTitleOrMetaTitle, resolveCmsPageLocalizedHtml } from '../data/domains/config';
import { useOrganizationSettings } from '../data/domains/config';
import { useTranslation } from '../shared/i18n';
import { useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { androidAppHeaderContentTop } from '../shared/theme/sharedStyles';
import {
  StackScreenShell,
  studioCardShell,
  DASH_STUDIO_RADIUS,
  STACK_SCROLL_GUTTER,
  STACK_SECTION_GAP,
} from '../shared/theme/studio/stackScreenShell';

function createAboutUsStyles(c: ThemeColors) {
  return StyleSheet.create({
    content: {
      flex: 1,
    },
    scrollContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
      gap: STACK_SECTION_GAP,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 50,
    },
    bodyCard: {
      ...studioCardShell(c),
      borderRadius: DASH_STUDIO_RADIUS,
      padding: appTheme.spacing.lg,
    },
    sectionTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: c.text,
      marginTop: appTheme.spacing.lg,
      marginBottom: appTheme.spacing.sm,
    },
    sectionTitleFirst: {
      marginTop: 0,
    },
    paragraph: {
      fontSize: 15,
      lineHeight: 24,
      color: c.textSecondary,
      marginBottom: appTheme.spacing.md,
    },
    bodyScroll: {
      flex: 1,
    },
    bodyScrollContent: {
      paddingHorizontal: STACK_SCROLL_GUTTER,
    },
    cmsBodyText: {
      fontSize: 15,
      lineHeight: 24,
      color: c.textSecondary,
    },
  });
}

export const AboutUsScreen: React.FC = () => {
  const on_back_exit = useExitOrGoBack();
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const { settings: app_settings } = useOrganizationSettings();
  const app_language = (app_settings?.appLanguage ?? app_settings?.language) === 'ar' ? 'ar' : 'en';
  const [content, setContent] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const content_top = androidAppHeaderContentTop(insets.top);
  const { scrollY, onScroll } = useScreenHeaderScroll();

  const colors = useThemeColors() ?? appTheme.colors;
  const styles = useMemo(() => createAboutUsStyles(colors), [colors]);

  useEffect(() => {
    fetchContent();
  }, [app_language]);

  const fetchContent = async () => {
    try {
      const pages = await fetchCmsPages();
      const about_page = findCmsPageByTitleOrMetaTitle(pages, 'About Us', 'aboutus');
      if (about_page) {
        setContent(resolveCmsPageLocalizedHtml(about_page, app_language));
      }
    } catch (_err) {
      setContent('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StackScreenShell colors={colors}>
      <ScreenHeader title={t('account.aboutUs')} onBack={on_back_exit} scrollY={scrollY} />
      {isLoading ? (
        <View style={[styles.loadingContainer, { paddingTop: content_top }]}>
          <MainLoader size={48} color={colors.primary} />
        </View>
      ) : content && cmsPageHtmlToDisplayText(content) ? (
        <CmsPagesApiHtmlScroll
          html={content}
          scrollViewStyle={styles.bodyScroll}
          onScroll={onScroll}
          scrollEventThrottle={16}
          contentContainerStyle={[
            styles.bodyScrollContent,
            {
              paddingTop: content_top,
              paddingBottom: insets.bottom + STACK_SECTION_GAP * 2,
            },
          ]}
          textStyle={styles.cmsBodyText}
        />
      ) : (
        <Animated.ScrollView
          style={styles.content}
          onScroll={onScroll}
          scrollEventThrottle={16}
          contentContainerStyle={[
            styles.scrollContent,
            {
              paddingTop: content_top,
              paddingBottom: insets.bottom + STACK_SECTION_GAP * 2,
            },
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          <View style={styles.bodyCard}>
            <Text style={[styles.sectionTitle, styles.sectionTitleFirst]}>Our Mission</Text>
            <Text style={styles.paragraph}>
              We are dedicated to making healthy eating simple, convenient, and delicious. Our mission is to help you achieve your health and fitness goals through carefully crafted meal plans
              tailored to your needs.
            </Text>

            <Text style={styles.sectionTitle}>What We Offer</Text>
            <Text style={styles.paragraph}>
              • Customized meal plans based on your dietary preferences{'\n'}• Fresh, nutritious meals prepared daily
              {'\n'}• Flexible subscription options{'\n'}• Convenient delivery to your doorstep{'\n'}• Expert nutritional guidance
            </Text>

            <Text style={styles.sectionTitle}>Our Team</Text>
            <Text style={styles.paragraph}>
              Our team consists of experienced chefs, nutritionists, and health experts who work together to create balanced, flavorful meals that support your wellness journey.
            </Text>

            <Text style={styles.sectionTitle}>Contact Us</Text>
            <Text style={styles.paragraph}>
              Have questions or feedback? We'd love to hear from you!{'\n\n'}
              Email: support@example.com{'\n'}
              Phone: +965 XXXX XXXX{'\n'}
              Working Hours: 9 AM - 9 PM
            </Text>
          </View>
        </Animated.ScrollView>
      )}
    </StackScreenShell>
  );
};

export default AboutUsScreen;
